import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

/**
 * BSG High Contrast Mode Service
 * Provides functionality to toggle high contrast mode across the application
 */
@Injectable({
	providedIn: 'root',
})
export class BsgHighContrastService {
	private renderer: Renderer2;
	private isHighContrastMode = false;
	private readonly STORAGE_KEY = 'bsg-high-contrast-mode';
	private readonly HIGH_CONTRAST_CLASS = 'bsg-high-contrast';

	constructor(private rendererFactory: RendererFactory2) {}

	/**
	 * Toggle high contrast mode on/off
	 * @returns Current state of high contrast mode after toggle
	 */
	public toggleHighContrast(): boolean {
		this.isHighContrastMode = !this.isHighContrastMode;
		this.applyHighContrastMode();
		this.savePreference();
		return this.isHighContrastMode;
	}

	/**
	 * Get current high contrast mode state
	 */
	public isHighContrast(): boolean {
		return this.isHighContrastMode;
	}

	/**
	 * Enable high contrast mode
	 */
	public enableHighContrast(): void {
		if (!this.isHighContrastMode) {
			this.isHighContrastMode = true;
			this.applyHighContrastMode();
			this.savePreference();
		}
	}

	/**
	 * Disable high contrast mode
	 */
	public disableHighContrast(): void {
		if (this.isHighContrastMode) {
			this.isHighContrastMode = false;
			this.applyHighContrastMode();
			this.savePreference();
		}
	}

	/**
	 * Apply high contrast mode to the document
	 */
	private applyHighContrastMode(): void {
		if (this.isHighContrastMode) {
			this.renderer.addClass(document.body, this.HIGH_CONTRAST_CLASS);
		} else {
			this.renderer.removeClass(document.body, this.HIGH_CONTRAST_CLASS);
		}
	}

	/**
	 * Save the user's preference
	 */
	private savePreference(): void {
		try {
			localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.isHighContrastMode));
		} catch (e) {
			console.warn('Could not save high contrast preference to localStorage');
		}
	}

	/**
	 * Load the user's saved preference
	 */
	private loadSavedPreference(): void {
		try {
			const savedPreference = localStorage.getItem(this.STORAGE_KEY);
			if (savedPreference !== null) {
				this.isHighContrastMode = JSON.parse(savedPreference);
				this.applyHighContrastMode();
			}
		} catch (e) {
			console.warn('Could not load high contrast preference from localStorage');
		}
	}
}
